import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { BpasEngagement } from '../../domain/bpas-engagement.model';
import * as EngagementsAPIActions from './engagements-api.actions';
import * as EngagementsUIActions from './engagements-ui.actions';

export interface EngagementsState extends EntityState<BpasEngagement> {
	isLoading: boolean;
	isUpdating: boolean;
	selectedEngagementId: string;
}

export function selectEngagementId(engagement: BpasEngagement): string {
	return engagement.uniqueId;
}

export const adapter: EntityAdapter<BpasEngagement> = createEntityAdapter<BpasEngagement>({
	selectId: selectEngagementId
});

export const initialState: EngagementsState = adapter.getInitialState({
	isLoading: false,
	isUpdating: false,
	selectedEngagementId: ''
});

const engagementsReducer = createReducer(
	initialState,
	on(
		EngagementsUIActions.loadEngagementsRequested,
		EngagementsUIActions.addEngagementRequested,
		EngagementsUIActions.loadOneEngagementRequested,
		EngagementsUIActions.closeActiveEngagementsAndStartNewEngagementRequested,
		state => ({ ...state, isLoading: true })
	),
	on(
		EngagementsUIActions.updateEngagementRequested,
		EngagementsUIActions.removePortfolioFromEngagementRequested,
		state => ({ ...state, isUpdating: true })
	),
	on(
		EngagementsAPIActions.loadEngagementsSucceeded,
		(state, { engagements }) => {
			state = { ...state, isLoading: false };
			return adapter.upsertMany(engagements, state);
		}
	),
	on(
		EngagementsAPIActions.updateEngagementsSucceeded,
		(state, { engagements }) => {
			state = { ...state, isUpdating: false, isLoading: false };
			return adapter.upsertMany(engagements, state);
		}
	),
	on(
		EngagementsAPIActions.closeEngagementsSucceeded,
		(state, { engagements }) => {
			return adapter.upsertMany(engagements, state);
		}
	),
	on(
		EngagementsAPIActions.openNewEngagementSucceeded,
		(state, { engagement }) => {
			state = { ...state, isLoading: false, selectedEngagementId: engagement.uniqueId };
			return adapter.upsertOne(engagement, state);
		}
	),
	on(
		EngagementsUIActions.updateEngagementAsyncRequested,
		(state, { engagement }) => {
			state = { ...state, isUpdating: false };
			return adapter.upsertOne(engagement, state);
		}
	),
	on(
		EngagementsAPIActions.loadEngagementsFailed,
		EngagementsAPIActions.updateEngagementsFailed,
		EngagementsAPIActions.removePortfolioFromEngagementFailed,
		state => ({
			...state,
			isLoading: false,
			isUpdating: false
		})
	),
	on(
		EngagementsUIActions.updateSelectedEngagementIdRequested,
		(state, { engagementId }) => ({
			...state,
			selectedEngagementId: engagementId
		})
	)
);

export function bpasEngagementReducer(state: EngagementsState | undefined, action: Action): EngagementsState {
	return engagementsReducer(state, action);
}

export const getIsLoading = (state: EngagementsState) => state.isLoading;

export const getIsUpdating = (state: EngagementsState) => state.isUpdating;

export const getSelectedEngagementId = (state: EngagementsState) => state.selectedEngagementId;

const {
	selectAll
} = adapter.getSelectors();

export const selectEngagements = selectAll;
